import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { calculateLimitationPeriod, askAboutDocument } from '../services/api';
import toast from 'react-hot-toast';

const DISPUTE_TYPES = [
  'Simple contract breach', 'Personal injury', 'Property damage', 'Employment dispute',
  'Professional negligence', 'Defamation', 'Fraud / misrepresentation', 'Debt recovery',
  'Family / matrimonial', 'IP infringement',
];

export default function LegalToolsPage() {
  const { currentUser } = useAuth();
  const [tool, setTool] = useState<'limitation' | 'glossary' | null>(null);

  // Limitation period tool
  const [limForm, setLimForm] = useState({ jurisdiction: 'HK', dispute_type: '', incident_date: '' });
  const [limResult, setLimResult] = useState('');
  const [limLoading, setLimLoading] = useState(false);

  // Glossary tool
  const [term, setTerm] = useState('');
  const [glossResult, setGlossResult] = useState('');
  const [glossLoading, setGlossLoading] = useState(false);

  const handleLimitation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    setLimLoading(true); setLimResult('');
    try {
      const token = await currentUser.getIdToken();
      const res = await calculateLimitationPeriod(token, limForm);
      setLimResult(res.result);
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setLimLoading(false); }
  };

  const handleGlossary = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !term.trim()) return;
    setGlossLoading(true); setGlossResult('');
    try {
      const token = await currentUser.getIdToken();
      const res = await askAboutDocument(token, `Explain the legal term "${term}" in plain English, with examples relevant to Hong Kong law.`);
      setGlossResult(res.answer);
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setGlossLoading(false); }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Legal Tools</h1>
        <p className="text-gray-500 text-sm mt-1">Practical calculators and references for common legal questions.</p>
      </div>

      {/* Tool selector */}
      <div className="grid grid-cols-2 gap-4">
        {[
          { id: 'limitation', icon: '⏰', title: 'Limitation Period', desc: 'Calculate when your right to sue expires' },
          { id: 'glossary', icon: '📖', title: 'Legal Glossary', desc: 'Explain any legal term in plain English' },
        ].map(({ id, icon, title, desc }) => (
          <button key={id} onClick={() => setTool(id as any)}
            className={`text-left rounded-2xl border p-5 transition-colors ${
              tool === id ? 'bg-blue-50 border-blue-400' : 'bg-white border-gray-200 hover:border-blue-300'
            }`}>
            <div className="text-3xl mb-2">{icon}</div>
            <p className="font-semibold text-gray-800">{title}</p>
            <p className="text-xs text-gray-500 mt-1">{desc}</p>
          </button>
        ))}
      </div>

      {/* Limitation Period Tool */}
      {tool === 'limitation' && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-800 mb-4">⏰ Limitation Period Calculator</h2>
          <form onSubmit={handleLimitation} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jurisdiction</label>
                <select value={limForm.jurisdiction} onChange={(e) => setLimForm((f) => ({ ...f, jurisdiction: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {['HK', 'UK', 'US', 'SG', 'AU'].map((j) => <option key={j} value={j}>{j}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Incident / Breach Date</label>
                <input type="date" value={limForm.incident_date} onChange={(e) => setLimForm((f) => ({ ...f, incident_date: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type of Dispute</label>
              <select value={limForm.dispute_type} onChange={(e) => setLimForm((f) => ({ ...f, dispute_type: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required>
                <option value="">Select dispute type</option>
                {DISPUTE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <button type="submit" disabled={limLoading || !limForm.dispute_type}
              className="px-6 py-2.5 bg-blue-700 text-white rounded-lg text-sm font-semibold hover:bg-blue-600 disabled:opacity-50 transition-colors">
              {limLoading ? 'Calculating...' : 'Calculate Limitation Period'}
            </button>
          </form>
          {limResult && (
            <div className="mt-4 bg-slate-50 rounded-xl p-4 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap border border-slate-200">
              {limResult}
            </div>
          )}
        </div>
      )}

      {/* Glossary Tool */}
      {tool === 'glossary' && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-800 mb-4">📖 Legal Glossary</h2>
          <form onSubmit={handleGlossary} className="flex gap-2">
            <input type="text" value={term} onChange={(e) => setTerm(e.target.value)}
              placeholder="Enter a legal term (e.g. indemnity, force majeure, estoppel)"
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <button type="submit" disabled={glossLoading || !term.trim()}
              className="px-5 py-2.5 bg-blue-700 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50 transition-colors">
              {glossLoading ? '...' : 'Explain'}
            </button>
          </form>
          {glossResult && (
            <div className="mt-4 bg-slate-50 rounded-xl p-4 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap border border-slate-200">
              {glossResult}
            </div>
          )}
        </div>
      )}

      {/* More tools teaser */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[
          { icon: '💰', title: 'Court Fee Estimator', status: 'Coming soon' },
          { icon: '📋', title: 'NDA Generator', status: 'Coming soon' },
          { icon: '⚖', title: 'Case Strength Scorer', status: 'Coming soon' },
          { icon: '🗓', title: 'Legal Deadline Tracker', status: 'Coming soon' },
          { icon: '🌏', title: 'Jurisdiction Comparator', status: 'Coming soon' },
          { icon: '📊', title: 'Settlement Calculator', status: 'Coming soon' },
        ].map(({ icon, title, status }) => (
          <div key={title} className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-4 opacity-60">
            <div className="text-2xl mb-1">{icon}</div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-xs text-gray-400">{status}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
